import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext} from 'react-hook-form';
import {PATTERNS} from '@chekin/core';
import {FormTypes} from '../../types';
import {CREATE_ACCOUNT_STEPS, FORM_NAMES} from '../../constants';
import {PATHS} from 'routes/constants';
import {useSignIn} from 'hooks/auth/useSignIn';
import {useSignUp} from 'hooks/auth/useSignUp';
import {usePath} from 'hooks';
import {useRedirectAuth} from 'hooks/auth/useRedirectAuth';
import {EmailCodeInput, InputController as Input} from '@guestapp/old-ui-kit';
import {ContinueButton, ErrorMessage, Form, StyledLinkButton} from './styled';

type Props = {
  step: CREATE_ACCOUNT_STEPS;
  onConfirm: (nextStep: CREATE_ACCOUNT_STEPS) => void;
  onGoBack: () => void;
  onResendEmail: () => void;
  onSuccessAuth: () => void;
  isLogin?: boolean;
  onLoadingChange: (isLoading: boolean) => void;
};

export const AccountForm = ({
  step,
  onConfirm,
  onGoBack,
  onResendEmail,
  isLogin,
  onSuccessAuth,
  onLoadingChange,
}: Props) => {
  const [loginError, setLoginError] = useState('');
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();

  const isEmailStep = step === CREATE_ACCOUNT_STEPS.email;
  const isPasswordStep = step === CREATE_ACCOUNT_STEPS.password;
  const isCheckEmailCodeStep = step === CREATE_ACCOUNT_STEPS.checkEmailCode;

  useRedirectAuth();

  const {
    handleSubmit,
    register,
    control,
    formState: {errors, isValid},
    getValues,
  } = useFormContext<FormTypes>();

  const {signIn, isPending: isSignInLoading} = useSignIn({
    onSuccess: () => {
      setLoginError('');
      onSuccessAuth();
    },
    onInvalid: () => setLoginError(t('invalid_email_or_password')),
  });

  const {
    signUp,
    confirmSignUp,
    isLoading: isSignUpLoading,
  } = useSignUp({
    onSignUpSuccess: () => {
      onConfirm(CREATE_ACCOUNT_STEPS.checkEmailCode);
      onLoadingChange(false);
    },
    onSignUpConfirmationSuccess: () => {
      navigate(getFullPath(PATHS.auth.welcome));
    },
  });

  const isLoading = isSignInLoading || isSignUpLoading;

  const onSubmit = useCallback(
    (data: FormTypes) => {
      setLoginError('');

      if (isEmailStep) {
        onConfirm(CREATE_ACCOUNT_STEPS.password);
        return;
      }
      if (isPasswordStep) {
        if (isLogin) {
          signIn({email: data.email, password: data.password});
        } else {
          signUp({email: data.email, password: data.password});
        }
        return;
      }

      if (isCheckEmailCodeStep) {
        const code = getValues(FORM_NAMES.email_code);
        confirmSignUp({code});
        return;
      }

      if (isValid) {
        onConfirm(CREATE_ACCOUNT_STEPS.email);
      }
    },
    [
      isEmailStep,
      isPasswordStep,
      isCheckEmailCodeStep,
      isValid,
      onConfirm,
      isLogin,
      signIn,
      getValues,
      signUp,
      confirmSignUp,
    ],
  );

  const handleSecondaryButton = () => {
    setLoginError('');

    switch (step) {
      case CREATE_ACCOUNT_STEPS.password:
        onGoBack();
        return;
      case CREATE_ACCOUNT_STEPS.checkEmailCode:
        onResendEmail();
        return;
      default:
        return;
    }
  };

  useEffect(
    function onLoadingChangeEffect() {
      onLoadingChange(isLoading);
    },
    [onLoadingChange, isLoading],
  );

  return (
    <Form $isPasswordStep={!isEmailStep || isLogin} onSubmit={handleSubmit(onSubmit)}>
      {(isEmailStep || isPasswordStep) && (
        <Input
          control={control}
          type="email"
          label={t('email')}
          {...register(FORM_NAMES.email, {
            required: t('required'),
            pattern: {
              value: PATTERNS.email,
              message: t('invalid_email'),
            },
          })}
          error={errors.email?.message}
          disabled={isLoading}
          autoCorrect="off"
          spellCheck={false}
        />
      )}
      {isPasswordStep && (
        <Input
          control={control}
          type="password"
          label={t('password')}
          {...register(FORM_NAMES.password, {
            required: t('required'),
          })}
          error={errors.password?.message}
          disabled={isLoading}
        />
      )}
      {isCheckEmailCodeStep && (
        <Controller
          name={FORM_NAMES.email_code}
          control={control}
          rules={{required: t('required')}}
          defaultValue=""
          render={({field}) => (
            <EmailCodeInput
              value={field.value || ''}
              onChange={field.onChange}
              errorMessage={errors.email_code?.message}
            />
          )}
        />
      )}
      {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
      <ContinueButton
        label={t('continue')}
        primary
        type="submit"
        disabled={isLoading}
        $isPasswordStep={!isEmailStep}
      />
      {(isPasswordStep || isCheckEmailCodeStep) && (
        <StyledLinkButton
          link
          label={isPasswordStep ? t('go_back') : t('resend_email')}
          onClick={handleSecondaryButton}
        />
      )}
    </Form>
  );
};
